import { useContextProvider } from "./../context/providers/ContextProvider";
import {
  createNewAlert,
  deleteAlert,
  getAlertActions,
  getAlertMeasures,
  getAllAlertsCsv,
  getBundleAlertGlobal,
  getBundleAlertsAccount,
  getBundleAlertsIndividual,
  getProductTypes,
  getUsageAlertById,
  getUsageAlerts,
  getUsageAlertsByAsset,
  getUsageAlertsHistory,
  updateBulkUsageAlerts,
  updateBundleAlertsAccounts,
  updateBundleAlertsAccountsDisableAll,
  updateBundleAlertsAccountsEnableAll,
  updateBundleAlertsIndividuals,
  updateBundleAlertsIndividualsDisableAll,
  updateBundleAlertsindividualsEnableAll,
  updateUsageAlert,
} from "./api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useInfiniteQuery, useQuery } from "../_app/lib/query";
import { AjaxResponse, IdValueQuery } from "../_app/api";
import {
  BillingAlertBulkEdit,
  BundleAlertUpdate,
  CreateUsageAlert,
  GetUsageAlertsHistoryResponse,
  GetUsageAlertsResponse,
  UpdateUsageAlert,
} from "./types";
import { useFeedbackAlerts } from "../_app/hooks";
import { alertRequestQueuedMessage, genericError } from "../_app/utils/text";
import { Query } from "../filter/types";
import { useNavigate } from "react-router-dom";

export function useUsageAlerts(limit: number, queries: any[], options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useInfiniteQuery<GetUsageAlertsResponse, AjaxResponse>({
    queryKey: ["usageAlerts", { accountId, queries }],
    queryFn: ({ pageParam }) => getUsageAlerts(accountId, pageParam as string, limit, queries),
    initialPageParam: undefined,
    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },
    getNextPageParam: (lastPage: any) => {
      return lastPage?.nextPageToken || undefined;
    },
    ...options,
  });
}

export function useUsageAlertsHistory(limit: number, queries: Query[], options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useInfiniteQuery<GetUsageAlertsHistoryResponse, AjaxResponse>({
    queryKey: ["usageAlertsHistory", { accountId, limit, queries }],
    queryFn: ({ pageParam }) => getUsageAlertsHistory(accountId, pageParam as string, limit, queries),
    initialPageParam: undefined,
    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },
    getNextPageParam: (lastPage: any) => {
      return lastPage?.nextPageToken || undefined;
    },
    ...options,
  });
}

export function useSingleUsageAlert(id: string = "", options = {}) {
  return useQuery({
    queryKey: ["usageAlert", id],
    queryFn: () => getUsageAlertById(id),
    ...options,
  });
}

export function useAssetUsageAlerts(assetId: string, limit: number, queries: any[], options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();

  return useInfiniteQuery<GetUsageAlertsResponse, AjaxResponse>({
    queryKey: ["usageAlertsByAssetId", { assetId, limit, queries }],
    queryFn: ({ pageParam }) => getUsageAlertsByAsset(assetId, pageParam as string, limit, queries),
    initialPageParam: undefined,
    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },
    getNextPageParam: (lastPage: any) => {
      return lastPage?.nextPageToken || undefined;
    },
    ...options,
  });
}

export function useProductTypes(options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["usageAlertProductTypes", accountId],
    queryFn: () => getProductTypes(accountId),
    ...options,
  });
}

export function useAlertActions(id: string, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["usageAlertActions", accountId, id],
    queryFn: () => getAlertActions({ product: id, accountId }),
    ...options,
  });
}

export function useAlertMeasures(product?: string, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["usageAlertMeasures", product, accountId],
    queryFn: () => getAlertMeasures({ product, accountId }),
    ...options,
  });
}

export function useCreateNewAlert(options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();

  return useMutation({
    mutationFn: (alert: CreateUsageAlert) => createNewAlert(alert),
    ...options,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["usageAlerts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });
      setFeedbackAlertSuccess(alertRequestQueuedMessage);
      options?.onSuccess?.();
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useUpdateUsageAlert(id: string = "", options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();

  return useMutation({
    mutationFn: (usageAlert: UpdateUsageAlert) => updateUsageAlert(usageAlert),
    ...options,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["usageAlerts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["usageAlert", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });
      setFeedbackAlertSuccess(alertRequestQueuedMessage);
      options?.onSuccess?.();
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useDeleteAlerts(options = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();

  return useMutation({
    mutationFn: (alert: string[]) => deleteAlert(alert),
    ...options,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["usageAlerts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });
      setFeedbackAlertSuccess(alertRequestQueuedMessage);
    },

    onError: (error: AjaxResponse) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },
  });
}

export function useBulkEditAlerts(options = {}) {
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (bulkUpdate: BillingAlertBulkEdit) => updateBulkUsageAlerts(bulkUpdate),
    ...options,

    onSuccess: () => {
      setFeedbackAlertSuccess(alertRequestQueuedMessage);
      queryClient.invalidateQueries({
        queryKey: ["usageAlerts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });
      navigate("/usage-alerts");
    },

    onError: (error: AjaxResponse) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },
  });
}

/* USAGE ALERTS END */

/* BUNDLE ALERTS START */

export function useBundleAlertsIndividual(page: number, limit: number, queries?: IdValueQuery[], options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["bundleAlertsIndividual", { accountId, page, limit, queries }],
    queryFn: () => getBundleAlertsIndividual(accountId, page, limit, queries),
    ...options,

    onError: () => {
      setFeedbackAlertError(genericError());
    },
  });
}

export function useBundleAlertsAccounts(page: number, limit: number, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["bundleAlertsAccount", { accountId, page, limit }],
    queryFn: () => getBundleAlertsAccount(accountId, page, limit),
    ...options,
  });
}

export function useBundleAlertGlobal() {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["bundleAlertGlobal", accountId],
    queryFn: () => getBundleAlertGlobal(accountId),

    onError: () => {
      setFeedbackAlertError(genericError());
    },
  });
}

function useUpdateBundleAlertsInternal(
  update: (bundleAlerts: BundleAlertUpdate[], accountId: string) => Promise<string>,
  options: any,
) {
  const queryClient = useQueryClient();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (bundleAlerts: { id: string; enabled: boolean }[]) => update(bundleAlerts, accountId),
    ...options,

    onSuccess: (data: string) => {
      queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });
      options?.onSuccess?.(data);
    },

    onError: (data: any) => {
      options?.onError?.(data);
    },
  });
}

export function useUpdateBundleAlertsAccounts(options?: any) {
  return useUpdateBundleAlertsInternal(updateBundleAlertsAccounts, options);
}

export function useUpdateBundleAlertsIndividuals(options?: any) {
  return useUpdateBundleAlertsInternal(updateBundleAlertsIndividuals, options);
}

function useUpdateBundleAlertsAccountsInternal(update: (accountId: string) => Promise<string>, options: any) {
  const queryClient = useQueryClient();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: () => update(accountId),
    ...options,

    onSuccess: (data: string) => {
      queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });
      options?.onSuccess?.(data);
    },

    onError: (data: any) => {
      options?.onError?.(data);
    },
  });
}

export function useUpdateBundleAlertsAccountsEnableAll(options?: any) {
  return useUpdateBundleAlertsAccountsInternal(updateBundleAlertsAccountsEnableAll, options);
}

export function useUpdateBundleAlertsAccountsDisableAll(options?: any) {
  return useUpdateBundleAlertsAccountsInternal(updateBundleAlertsAccountsDisableAll, options);
}

function useUpdateBundleAlertsIndividualsInternal(
  update: (accountId: string, filters?: IdValueQuery[] | undefined) => Promise<string>,
  options: any,
) {
  const queryClient = useQueryClient();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (filters: IdValueQuery[] | undefined) => update(accountId, filters),
    ...options,

    onSuccess: (data: string) => {
      queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });
      options?.onSuccess?.(data);
    },

    onError: (data: any) => {
      options?.onError?.(data);
    },
  });
}

export function useUpdateBundleAlertsIndividualsEnableAll(options?: any) {
  return useUpdateBundleAlertsIndividualsInternal(updateBundleAlertsindividualsEnableAll, options);
}

export function useUpdateBundleAlertsIndividualsDisableAll(options?: any) {
  return useUpdateBundleAlertsIndividualsInternal(updateBundleAlertsIndividualsDisableAll, options);
}

export function useExportAllAlerts(options: any = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["all-alerts-export", accountId],
    queryFn: () => getAllAlertsCsv(accountId),
    enabled: false,
    gcTime: 0,

    onError: () => {
      setFeedbackAlertError("Could not export alerts.");
    },

    ...options,
  });
}
